import React, {
	useState,
	useEffect,
	useLayoutEffect,
	useRef,
	useCallback,
	PropsWithChildren,
	ReactNode,
	ReactElement,
} from 'react';

import { TabButton } from './TabButton';
import styled, { css } from 'styled-components';
import { useUniqueId } from '../utils/useUniqueId';

import { scrollElementIntoView } from './scrollUtilities';
import { useDraggable } from 'react-use-draggable-scroll';
import { PropsWithClassName } from './PropsWithClassName';

const StyledTabsContainer = styled.div`
  border-bottom: 1px solid ${props => props.theme.border.decorative};
`;

const StyledTabButtons = styled.div<{
    $tabFill: boolean;
}>`
  display: flex;
  justify-content: ${props => props.$tabFill ? 'space-between' : 'flex-start'};
  width: 100%;
  overflow: auto;
  gap: 10px;
  ${({ $tabFill }) => $tabFill && css`
    & > span {
      flex: 1 auto;
      display: flex;
    }
  `};

`;

const tabButtonClassName = (pref: string, id: string) => `${pref}-tab-button-${id}`;

type TabSelectorProps = PropsWithChildren<PropsWithClassName<{
    selectedTab?: string,
    autoScroll?: boolean,
    tabFill?: boolean,
    scrollable?: boolean,
    errors?: string[],
    onTabChange?: (id: string) => void,
    children: ReactElement | ReactElement[];
}>>

export function TabSelector({
	className,
	tabFill = true,
	selectedTab,
	autoScroll = true,
	scrollable = true,
	errors,
	onTabChange,
	children
}: TabSelectorProps) {
	const selectorRef = useRef() as React.MutableRefObject<HTMLInputElement>;

	const elements = React.Children.toArray(children).filter(child => React.isValidElement(child)) as ReactElement[];

	const selectorId = useUniqueId('tab-selector-');
	const tabIds: string[] = elements.map(({ props }) => props.id);

	const [tab, setTab] = useState(selectedTab || tabIds[0]);
	const normalizedTab = tabIds.indexOf(tab) >= 0 ? tab : tabIds[0];
	const { events } = useDraggable(selectorRef);

	const canScroll: boolean = Boolean(tab) && autoScroll && scrollable;

	useEffect(() => {
		if (selectedTab) {
			setTab(selectedTab);
		}
	}, [selectedTab]);

	useLayoutEffect(() => {
		if (canScroll) {
			scrollElementIntoView(`.${tabButtonClassName(selectorId, tab)}`);
		}
	}, [selectorId, canScroll, tab]);

	const onSelect = useCallback((id: string) => {
		setTab(id);
		if (onTabChange)
			onTabChange(id);
	}, [onTabChange]);

	let currentTab: ReactNode | null = null;

	const buttons = elements.map((element) => {
		const {
			id,
			['data-disabled']: disabled,
			['data-name']: label,
			['data-icon']: icon,
			['data-chip']: chip,
			['data-message']: message,
		} = element.props;
		let selected = id === normalizedTab;
		const isError = errors?.includes(id) ?? false;

		if (!normalizedTab && !currentTab) {
			selected = true;
		}
		if (selected) {
			currentTab = element;
		}
		return <TabButton
			key={id}
			id={id}
			icon={icon}
			chip={chip}
			className={tabButtonClassName(selectorId, id)}
			label={label}
			message={message}
			disabled={disabled}
			selected={selected}
			error={isError}
			onSelect={onSelect} />;
	});

	return <>
		<StyledTabsContainer className={className}>
			<StyledTabButtons
				ref={selectorRef}
				$tabFill={tabFill}
				className={'tab-buttons-container'}
				{...(scrollable ? events : {})}
			>
				{buttons}
			</StyledTabButtons>
		</StyledTabsContainer>
		{currentTab}
	</>;
}
